// Route Configurations Export
import bureauRoutes from './bureauRoutes';
import adminRoutes from './adminRoutes';
import clientRoutes from './clientRoutes';
import superAdminRoutes from './superAdminRoutes';
import supportRoutes from './supportRoutes';
import tiplockRoutes from './tiplockRoutes';
import salesRoutes from './salesRoutes';
import technicianRoutes from './technicianRoutes';
import insuranceRoutes from './insuranceRoutes';
import {
  commonRoutes,
  portalRedirects,
  getDefaultRedirectForUser,
  validateRouteAccess,
} from './commonRoutes';

// Re-export for external use
export {
  bureauRoutes,
  adminRoutes,
  clientRoutes,
  superAdminRoutes,
  supportRoutes,
  tiplockRoutes,
  salesRoutes,
  technicianRoutes,
  insuranceRoutes,
};
export { commonRoutes, portalRedirects, getDefaultRedirectForUser, validateRouteAccess };

/**
 * All Portal Routes Configuration
 * Organized by portal type for easy management
 */
export const allPortalRoutes = [
  bureauRoutes,
  adminRoutes,
  clientRoutes,
  superAdminRoutes,
  supportRoutes,
  tiplockRoutes,
  salesRoutes,
  technicianRoutes,
  insuranceRoutes,
];

/**
 * Route Configuration Summary
 * Useful for debugging and route management
 */
export const routeConfigSummary = {
  bureau: {
    path: '/bureau',
    name: 'Bureau Portal',
    totalRoutes: bureauRoutes.children?.length || 0,
    requiredRoles: bureauRoutes.requiredRoles,
  },
  admin: {
    path: '/admin',
    name: 'Admin Portal',
    totalRoutes: adminRoutes.children?.length || 0,
    requiredRoles: adminRoutes.requiredRoles,
  },
  client: {
    path: '/client',
    name: 'Client Portal',
    totalRoutes: clientRoutes.children?.length || 0,
    requiredRoles: clientRoutes.requiredRoles,
  },
  superAdmin: {
    path: '/super-admin',
    name: 'Super Admin Portal',
    totalRoutes: superAdminRoutes.children?.length || 0,
    requiredRoles: superAdminRoutes.requiredRoles,
  },
  tiplock: {
    path: '/tiplock',
    name: 'TipLock Portal',
    totalRoutes: tiplockRoutes.children?.length || 0,
    requiredRoles: tiplockRoutes.requiredRoles,
  },
  sales: {
    path: '/sales',
    name: 'Sales Portal',
    totalRoutes: salesRoutes.children?.length || 0,
    requiredRoles: salesRoutes.requiredRoles,
  },
  common: {
    name: 'Common Routes',
    totalRoutes: commonRoutes.length,
    description: 'Shared routes and utilities',
  },
};

/**
 * Get all routes flattened for route generation
 */
export const getAllRoutes = () => {
  return [...commonRoutes, ...allPortalRoutes];
};

/**
 * Get navigation items for a specific portal
 */
export const getNavigationForPortal = (portalName) => {
  const portal = allPortalRoutes.find((p) => p.path.includes(portalName.toLowerCase()));

  if (!portal || !portal.children) {
    return [];
  }

  return portal.children
    .filter((route) => !route.meta?.hideInNavigation)
    .map((route) => ({
      path: `${portal.path}${route.path ? `/${route.path}` : ''}`,
      name: route.name,
      icon: route.meta?.icon,
      requiredPermissions: route.requiredPermissions,
      children:
        route.children
          ?.filter((child) => !child.meta?.hideInNavigation)
          .map((child) => ({
            path: `${portal.path}/${route.path}/${child.path}`,
            name: child.name,
            requiredPermissions: child.requiredPermissions,
          })) || [],
    }));
};

/**
 * Get breadcrumb trail for a given path
 */
export const getBreadcrumbTrail = (pathname) => {
  const breadcrumbs = [{ name: 'Home', path: '/' }];

  // Find the portal
  const portal = allPortalRoutes.find((p) => pathname.startsWith(p.path));
  if (!portal) return breadcrumbs;

  breadcrumbs.push({ name: portal.name, path: portal.path });

  // Parse the remaining path
  const remainingPath = pathname.replace(portal.path, '').split('/').filter(Boolean);
  let currentPath = portal.path;
  let currentChildren = portal.children;

  for (const segment of remainingPath) {
    const route = currentChildren?.find((r) => r.path === segment || r.index);
    if (route) {
      currentPath += route.path ? `/${route.path}` : '';
      breadcrumbs.push({
        name: route.meta?.breadcrumb || route.name,
        path: currentPath,
      });
      currentChildren = route.children;
    }
  }

  return breadcrumbs;
};

export default getAllRoutes;
